import * as Sentry from '@sentry/nextjs';
import { VASTClient, VASTTracker } from './lib';
import { injectScriptTag, getLocalISOString, convertTimeOffsetToSeconds, isHttpUrl } from './lib';
import { playLinearAd, playNonLinearAd, playCompanionAd } from './modes';
import { announceAutoplay, layersZIndex } from '../../../Player/PlayerServices';
import { addIcons, handleVMAP, parseInlineVastData } from './features';

// Holds a reference to the current ad for skip-button access
let globalCurrentAd = null;

// VMAP time offsets of a preroll and a postroll. Every other break is a mid-roll, so no offset
// can be both (a 00:00:00 preroll played twice, once more as a mid-roll at 0 s).
const PREROLL_OFFSETS = ['start', '0%', '00:00:00', '00:00:00.000'];
const POSTROLL_OFFSETS = ['end', '100%'];

class SimpleVast {
  constructor(videoElement, options) {
    this.video = videoElement;       // Native HTMLVideoElement for content
    this.adVideo = null;             // Created on demand as an overlay for each ad
    this.isInAdMode = false;
    this._adStartFired = false;
    this._adErrorHandled = false;
    this._adLoadTimeout = null;

    this.adsArray = [];
    this.domElements = [];
    this.iconContainers = [];
    this.watchForProgress = [];
    this.postRollUrl = null;
    this.postRollData = null;
    this._skipInterval = null;
    this._midrollFiring = false;
    // Set by dispose(). VAST/VMAP fetches and the verification timer are async, so a break can
    // still be mid-flight when the host tears the plugin down — this stops it starting an ad
    // after that point.
    this._disposed = false;
    this._contentLoadRequested = false;
    this._isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    // Volume/mute is a single state shared by the content element and the ad overlay — the way
    // video.js keeps it on the player while contrib-ads swaps the ad source into the one element.
    // The content element is the source of truth (the control bar writes to it even mid-ad) and
    // the overlay follows it. Ads keep their own default until that volume has actually been set
    // once, so a break starting before anything has touched it — the viewer, or the muted
    // autoplay fallback below — doesn't inherit a mute nobody chose and play silently.
    this._contentVolumeTouched = false;

    const defaultOptions = {
      vastUrl: false,
      vmapUrl: false,
      verificationTimeout: 2000,
      addCtaClickZone: true,
      addSkipButton: true,
      debug: false,
      timeout: 15000,
      isLimitedTracking: false,
      getContentQuality: null,
      loadContent: null,
    };
    this.options = Object.assign(defaultOptions, options);

    this.setMacros();

    // Immediately show a black overlay to hide the content poster while VAST/VMAP loads
    this._adPlaceholder = null;
    if (this.video.parentElement) {
      this._adPlaceholder = document.createElement('div');
      this._adPlaceholder.style.cssText =
        `position:absolute;top:0;left:0;width:100%;height:100%;z-index:${layersZIndex.toolbar.adOverlay};background:#000`;
      this.video.parentElement.appendChild(this._adPlaceholder);
    }

    // Pause content immediately so that ads can play first
    if (!this.video.paused) this.video.pause();
    this._blockContentPlay = true;
    this.video.addEventListener('canplay', this._onContentCanPlay);
    this.video.addEventListener('play', this._onContentPlayAttempt);
    this.video.addEventListener('volumechange', this._onContentVolumeChange);

    if (options.vmapUrl) {
      this.handleVMAP(options.vmapUrl);
    } else {
      (async () => {
        await this.handleVAST(options.vastUrl, () => {
          this._resumeContent();
        });
        if (this.adsArray.length > 0) {
          this.addEventsListeners();
          this.readAd();
        }
      })();
    }
  }

  // ─── Ad-break lifecycle ──────────────────────────────────────────────────

  // Single source of truth: the content video must stay paused while ads are loading OR an ad
  // is active. The guard listeners below are kept attached for the whole session (removed in
  // dispose), so this holds for every ad break — the content can't play behind the ad (e.g.
  // progressing the movie during a pre-roll, or keeping playing when the user pauses the ad).
  _shouldBlockContent() {
    return this._blockContentPlay || this.isInAdMode;
  }

  _onContentCanPlay = () => {
    if (this._shouldBlockContent()) {
      this.video.pause();
    }
  };

  _onContentPlayAttempt = () => {
    if (!this._shouldBlockContent()) return;
    this.video.pause();
    // iOS only: once the content element finishes buffering it can grab playback focus and
    // pause the ad overlay. After re-pausing content, re-assert ad playback (the pre-roll ad
    // is muted, so this needs no gesture). play() on an already-playing element is a no-op.
    if (this._isIOS && this.isInAdMode && this.adVideo) {
      this.adVideo.play().catch((e) => this.debug('ad resume after content play attempt failed', e));
    }
  };

  _onContentVolumeChange = () => {
    this._contentVolumeTouched = true;
    this._syncAdVolumeFromContent();
  };

  // Mirrors the content element's volume/mute onto the ad overlay so muting carries in both
  // directions: into a break (the controls write to the content element even while an ad is on
  // screen) and back out of it (the content element already holds what the user chose).
  _syncAdVolumeFromContent() {
    if (!this.isInAdMode || !this.adVideo || !this._contentVolumeTouched) return;
    this.adVideo.volume = this.video.volume;
    this.adVideo.muted = this.video.muted;
  }

  // The mp4 twin of video.js's `autoplay: 'any'` retry, for the element the browser is asked to
  // start first: when autoplay with sound is refused, mute and try again rather than leave a
  // dead screen behind a manual-start button. The mute is written to the content element — the
  // source of truth the overlay mirrors — so UnmuteNotice offers the one click that lifts it
  // for the ad and the content alike. Returns whether the retry was worth making; `onRefused`
  // runs when even muted playback is turned down, which is where a caller's last resort goes.
  _retryPlaybackMuted(element, onRefused) {
    // Already fell back once (or the viewer muted it themselves): a second retry would change
    // nothing, and the refusal is about something other than the sound.
    if (this.video.muted) return false;
    this.video.muted = true; // the source of truth the overlay mirrors
    // ...but that mirroring rides on 'volumechange', which the browser fires asynchronously -
    // the retry below would still be asking for an unmuted start and be refused all over again.
    element.muted = true;
    element.play()
      .then(() => announceAutoplay(this.video, true))
      .catch(() => {
        announceAutoplay(this.video, false);
        onRefused?.();
      });
    return true;
  }

  // Builds the overlay <video> used for ads.
  _buildAdVideoElement() {
    const adVideo = document.createElement('video');
    adVideo.setAttribute('playsinline', '');
    adVideo.playsInline = true;
    adVideo.controls = false;
    adVideo.style.cssText = [
      'position:absolute', 'top:0', 'left:0', 'width:100%', 'height:100%',
      `z-index:${layersZIndex.toolbar.adOverlay}`, 'background:#000', 'object-fit:contain', 'display:none',
    ].join(';');
    return adVideo;
  }

  _enterAdMode() {
    if (this.isInAdMode) return;
    this.isInAdMode = true;
    this._adStartFired = false;
    this._adErrorHandled = false;
    this._adPauseTracked = false;

    if (!this.video.paused) this.video.pause();

    const wrapper = this.video.parentElement;
    if (!wrapper) return;

    // Remove loading placeholder — ad video takes over
    if (this._adPlaceholder) {
      this._adPlaceholder.remove();
      this._adPlaceholder = null;
    }

    // Reuse the persistent (already user-unlocked) ad element; recreate only if missing.
    if (!this.adVideo) {
      this.adVideo = this._buildAdVideoElement();
      wrapper.appendChild(this.adVideo);
    }
    this.adVideo.style.display = 'block';

    // Carry the current volume/mute in before the ad events are bound, so this initial sync
    // doesn't fire a mute/unmute tracking beacon of its own.
    this._syncAdVolumeFromContent();

    this._bindAdVideoEvents();
  }

  _exitAdMode() {
    if (!this.isInAdMode) return;
    this.isInAdMode = false;

    this._unbindAdVideoEvents();
    clearTimeout(this._adLoadTimeout);

    // Keep the element (it stays user-unlocked for later ad breaks); just reset and hide it.
    if (this.adVideo) {
      this.adVideo.pause();
      this.adVideo.removeAttribute('src');
      this.adVideo.load();
      this.adVideo.style.display = 'none';
    }
  }

  _resumeContent() {
    this._blockContentPlay = false;
    // Never resume content while an ad is still active (e.g. another ad in a pod is playing).
    // The guard listeners stay attached for the whole session (removed in dispose) so they
    // keep content paused for later ad breaks too.
    if (this.isInAdMode) return;
    if (this._adPlaceholder) {
      this._adPlaceholder.remove();
      this._adPlaceholder = null;
    }
    // First time the content is needed (no preroll is due, or the preroll break is over): a host that holds
    // the content source back attaches it now, and the play below waits for its loadeddata.
    if (!this._contentLoadRequested) {
      this._contentLoadRequested = true;
      this.options.loadContent?.();
    }
    // After a post-roll the movie has ended, and play() would start it again from 0:00.
    if (this.video.ended) return;
    // An ad break that turns out empty leaves this as the first playback the browser is asked
    // for, so it needs the same muted fallback the break itself would have had.
    const doPlay = () => {
      // Bound to loadeddata below, which can fire after the host disposed the plugin.
      if (this._disposed) return;
      this.video.play()
        .then(() => announceAutoplay(this.video, true))
        .catch((e) => {
          this.debug('play rejected', e);
          if (e?.name === 'NotAllowedError') this._retryPlaybackMuted(this.video);
        });
    };
    // Wait for loadeddata so Player.tsx's setResume can apply recommended_start first
    if (this.video.readyState < 2) {
      this.video.addEventListener('loadeddata', doPlay, { once: true });
    } else {
      doPlay();
    }
  }

  // Dispatches a CustomEvent on the content video element for consumers to listen to
  _trigger(name, data = {}) {
    this.video.dispatchEvent(
      new CustomEvent(`vast.${name}`, { detail: data, bubbles: true }),
    );
  }

  // ─── Ad video event binding ───────────────────────────────────────────────

  _bindAdVideoEvents() {
    if (!this.adVideo) return;
    // 'playing' — NOT 'play'. 'play' fires the instant play() is called, before any media
    // data arrives, so a dead src (e.g. 404) would clear the load timeout and fire start
    // beacons for an ad that never renders, leaving no watchdog on TVs (Tizen) that don't
    // reliably fire 'error'. 'playing' only fires once frames are actually rendering.
    this.adVideo.addEventListener('playing', this._onAdVideoPlay);
    this.adVideo.addEventListener('pause', this.onAdPause);
    this.adVideo.addEventListener('timeupdate', this.onAdTimeUpdate);
    this.adVideo.addEventListener('ended', this.onAdEnded);
    this.adVideo.addEventListener('error', this.onAdError);
    this.adVideo.addEventListener('volumechange', this.onAdVolumeChange);
  }

  _unbindAdVideoEvents() {
    if (!this.adVideo) return;
    this.adVideo.removeEventListener('playing', this._onAdVideoPlay);
    this.adVideo.removeEventListener('pause', this.onAdPause);
    this.adVideo.removeEventListener('timeupdate', this.onAdTimeUpdate);
    this.adVideo.removeEventListener('ended', this.onAdEnded);
    this.adVideo.removeEventListener('error', this.onAdError);
    this.adVideo.removeEventListener('volumechange', this.onAdVolumeChange);
  }

  // Single play handler — fires onAdStart once, then onAdPlay for every resume
  _onAdVideoPlay = () => {
    clearTimeout(this._adLoadTimeout);
    if (!this._adStartFired) {
      this._adStartFired = true;
      this.onAdStart();
    }
    this.onAdPlay();
  };

  // ─── VAST / VMAP fetching ────────────────────────────────────────────────

  setMacros(newMacros = undefined) {
    if (!newMacros) {
      const cacheBuster = parseInt(Date.now().toString().slice(-8), 10);
      const ts = getLocalISOString(new Date());
      this.macros = {
        CACHEBUSTING: cacheBuster,
        TIMESTAMP: ts,
        PAGEURL: window.location !== window.parent.location
          ? document.referrer
          : document.location.href,
        LIMITADTRACKING: this.options.isLimitedTracking,
      };
    } else {
      this.macros = { ...this.macros, ...newMacros };
    }
  }

  async handleVAST(vastUrl, onError = null) {
    this.vastClient = new VASTClient();
    try {
      const response = await this.vastClient.get(vastUrl);
      const ads = response.ads ?? [];
      // Appended: the preroll breaks of a VMAP are fetched in parallel into this one queue.
      this.adsArray.push(...ads);
      if (ads.length === 0) {
        onError?.();
        this._captureError('SimpleVast: Empty VAST XML', { tag: vastUrl });
        this._trigger('error', { message: 'SimpleVast: Empty VAST XML', tag: vastUrl });
        this.removeDomElements();
      }
    } catch (err) {
      onError?.();
      this._captureError('SimpleVast: Error fetching VAST XML', { error: err, tag: vastUrl });
      this._trigger('error', { message: 'SimpleVast: Error fetching VAST XML', tag: vastUrl });
      this.removeDomElements();
    }
  }

  removeDomElements() {
    this.domElements.forEach((el) => el.remove());
    this.clearSkipInterval();
    this.domElements = [];
  }

  // ─── Ad sequencing ────────────────────────────────────────────────────────

  readAd() {
    if (this._disposed) return;
    const currentAd = this.getNextAd();
    if (!currentAd) {
      // The break came back without an ad (no fill, a failed fetch, no ad source): let the next
      // mid-roll fire.
      this._midrollFiring = false;
      return;
    }
    globalCurrentAd = currentAd;
    // A fresh cache buster and timestamp for each ad's beacons.
    this.setMacros();

    this.ctaUrl = SimpleVast.getBestCtaUrl(currentAd.linearCreative());
    this.debug('ctaUrl', this.ctaUrl);

    if (currentAd.hasLinearCreative()) {
      this.linearVastTracker = new VASTTracker(
        this.vastClient,
        currentAd.ad,
        currentAd.linearCreative(),
      );
      this.linearVastTracker.on('firstQuartile', () => this.debug('firstQuartile'));
      this.linearVastTracker.on('midpoint', () => this.debug('midpoint'));

      this.addIcons(currentAd);
      this.addSkipButton(currentAd.linearCreative());

      if (currentAd.ad.adVerifications && currentAd.ad.adVerifications.length > 0) {
        // Scripts that finish after the timeout must not start the ad a second time.
        let started = false;
        const verificationTimeout = setTimeout(() => {
          started = true;
          this.playLinearAd(currentAd.linearCreative());
        }, this.options.verificationTimeout);

        let index = 0;
        this.setMacros({
          OMIDPARTNER: currentAd.ad.adVerifications[index].vendor ?? 'unknown',
        });

        const scriptTagCallback = () => {
          index++;
          if (index < currentAd.ad.adVerifications.length) {
            injectScriptTag(
              currentAd.ad.adVerifications[index].resource,
              scriptTagCallback,
              scriptTagErrorCallback,
            );
          } else {
            clearTimeout(verificationTimeout);
            if (!started) this.playLinearAd(currentAd.linearCreative());
          }
        };

        const scriptTagErrorCallback = () => {
          this.linearVastTracker.verificationNotExecuted(
            currentAd.ad.adVerifications[index].vendor,
            { REASON: 3 },
          );
          scriptTagCallback();
        };

        injectScriptTag(
          currentAd.ad.adVerifications[index].resource,
          scriptTagCallback,
          scriptTagErrorCallback,
        );
      } else {
        this.playLinearAd(currentAd.linearCreative());
      }
    } else {
      // Nothing to play in the overlay: go on with the break, or end it like any other.
      if (this.adsArray.length) this.readAd();
      else this.resetPlayer();
    }

    // Non-linear and companion ads play alongside (or instead of) linear
    if (currentAd.hasNonlinearCreative()) {
      const playNonLinear = () => {
        this.nonLinearVastTracker = new VASTTracker(
          this.vastClient, currentAd.ad, currentAd.nonlinearCreative(), 'NonLinearAd',
        );
        this.playNonLinearAd(currentAd.nonlinearCreative());
      };
      if (currentAd.hasLinearCreative()) {
        // Wait for ad video to start playing
        this.adVideo
          ? this.adVideo.addEventListener('play', playNonLinear, { once: true })
          : this.video.addEventListener('play', playNonLinear, { once: true });
      } else {
        this.video.addEventListener('play', playNonLinear, { once: true });
      }
    }

    if (currentAd.hasCompanionCreative()) {
      const playCompanion = () => {
        this.companionVastTracker = new VASTTracker(
          this.vastClient, currentAd.ad, currentAd.companionCreative(), 'CompanionAd',
        );
        this.playCompanionAd(currentAd.companionCreative());
      };
      if (currentAd.hasLinearCreative()) {
        this.adVideo
          ? this.adVideo.addEventListener('play', playCompanion, { once: true })
          : this.video.addEventListener('play', playCompanion, { once: true });
      } else {
        this.video.addEventListener('play', playCompanion, { once: true });
      }
    }
  }

  getNextAd() {
    if (this.adsArray.length === 0) return null;
    const nextAd = this.adsArray.shift();
    return {
      ad: nextAd,
      hasLinearCreative: () => {
        const linear = nextAd.creatives.find((c) => c.type === 'linear');
        return !!(linear && linear.mediaFiles.length > 0 &&
          linear.mediaFiles.some((mf) => mf.fileURL !== ''));
      },
      linearCreative: () => nextAd.creatives.find((c) => c.type === 'linear'),
      hasNonlinearCreative: () => nextAd.creatives.some((c) => c.type === 'nonlinear'),
      nonlinearCreative: () => nextAd.creatives.find((c) => c.type === 'nonlinear'),
      hasCompanionCreative: () => nextAd.creatives.some((c) => c.type === 'companion'),
      companionCreative: () => nextAd.creatives.find((c) => c.type === 'companion'),
    };
  }

  // ─── Ad event handlers ────────────────────────────────────────────────────

  onAdStart = () => {
    this.debug('onAdStart');
    this._trigger('play', {
      ctaUrl: this.ctaUrl,
      skipDelay: this.linearVastTracker.skipDelay,
      adClickCallback: this.ctaUrl ? () => this.adClickCallback(this.ctaUrl) : false,
    });

    this.linearVastTracker.load({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
    });
    this.linearVastTracker.trackImpression({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
    });
    this.linearVastTracker.overlayViewDuration(
      this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
      this.macros,
    );

    if (this.options.addCtaClickZone && this.ctaUrl) {
      const ctaDiv = document.createElement('div');
      ctaDiv.style.cssText =
        `position:absolute;bottom:3em;left:0;right:0;top:0;z-index:${layersZIndex.toolbar.bar};cursor:pointer`;
      ctaDiv.addEventListener('click', () => {
        this.adVideo?.pause();
        this.adClickCallback(this.ctaUrl);
      });
      this.domElements.push(ctaDiv);
      this.video.parentElement.appendChild(ctaDiv);
    }
  };

  onAdPlay = () => {
    this.debug('onAdPlay');
    // 'playing' also follows every rebuffer: only a pause that was reported has a resume.
    if (this._adPauseTracked) {
      this._adPauseTracked = false;
      this.linearVastTracker.setPaused(false, {
        ...this.macros,
        ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
      });
    }
  };

  onAdPause = () => {
    this.debug('onAdPause');
    this._trigger('pause', {
      ctaUrl: this.ctaUrl,
      skipDelay: this.linearVastTracker?.skipDelay,
      adClickCallback: this.ctaUrl ? () => this.adClickCallback(this.ctaUrl) : false,
    });

    const currentTime = this.adVideo?.currentTime ?? 0;
    const duration = this.adVideo?.duration ?? 0;

    // Don't track the pause event that fires just before complete
    if (duration - currentTime > 0.2) {
      this._adPauseTracked = true;
      this.linearVastTracker.setPaused(true, {
        ...this.macros,
        ADPLAYHEAD: this.linearVastTracker.convertToTimecode(currentTime),
      });
    }
  };

  onAdTimeUpdate = () => {
    const currentTime = this.adVideo?.currentTime ?? 0;
    const duration = this.adVideo?.duration ?? 0;

    this.linearVastTracker.setProgress(currentTime, this.macros);
    this._trigger('time', {
      position: currentTime,
      currentTime,
      duration,
    });
  };

  onProgress = async () => {
    if (this._midrollFiring || !this.watchForProgress?.length) return;

    const { timeOffset } = this.watchForProgress[0];
    const timeOffsetInSeconds = convertTimeOffsetToSeconds(timeOffset, this.video.duration);
    const remaining = timeOffsetInSeconds - this.video.currentTime;

    if (remaining <= 10 && remaining > 0) {
      this._trigger('showAdCounter', { counter: remaining });
    }

    if (this.video.currentTime > timeOffsetInSeconds) {
      this._midrollFiring = true;
      const nextAd = this.watchForProgress.shift();
      if (nextAd.vastUrl) {
        await this.handleVAST(nextAd.vastUrl);
        this.readAd();
      } else if (nextAd.vastData) {
        this.parseInlineVastData(nextAd.vastData, 'midroll');
      } else {
        this.readAd(); // no ad source: nothing to play, which releases the mid-roll lock
      }
    }
  };

  _onContentSeeking = () => {
    const currentTime = this.video.currentTime;
    let lastSmallerOrEqualIndex = -1;
    for (let i = 0; i < this.watchForProgress.length; i++) {
      if (convertTimeOffsetToSeconds(this.watchForProgress[i].timeOffset, this.video.duration) <= currentTime) {
        lastSmallerOrEqualIndex = i;
      }
    }
    if (lastSmallerOrEqualIndex > 0 && this.watchForProgress.length > 1) {
      this.watchForProgress = this.watchForProgress.slice(lastSmallerOrEqualIndex);
    }
  };

  onAdVolumeChange = () => {
    this.debug('onAdVolumeChange');
    if (!this.linearVastTracker || !this.adVideo) return;
    this.linearVastTracker.setMuted(this.adVideo.muted, {
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo.currentTime),
    });
  };

  onAdFullScreenChange = () => {
    this.debug('onAdFullScreenChange');
    // The tracker of the last ad outlives its break; the movie's own fullscreen isn't the ad's.
    if (!this.isInAdMode || !this.linearVastTracker) return;
    const isFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement
    );
    this.linearVastTracker.setFullscreen(isFullscreen);
  };

  onAdTimeout = () => {
    if (this._adStartFired) return;
    this.debug('onAdTimeout');
    // Suppress the late 'error' event some devices (e.g. Tizen) fire long after the fetch
    // failed — we're already advancing past this ad here, so it must not advance again.
    this._adErrorHandled = true;
    if (this.linearVastTracker) {
      this.linearVastTracker.error({ ...this.macros, ERRORCODE: 402 }); // timeout of MediaFile URI
    }
    this._captureError('SimpleVast: Ad media load timeout', {
      src: this.adVideo?.currentSrc || null,
      timeout: this.options.timeout,
      networkState: this.adVideo?.networkState ?? null,
      readyState: this.adVideo?.readyState ?? null,
    });
    this._trigger('error', { code: 408, message: 'SimpleVast: Timeout' });
    this.removeDomElements();

    // Skip to the next ad like onAdError does, rather than abandoning the whole break.
    // Note: content-level listeners stay attached so later midrolls still fire.
    if (this.adsArray.length === 0) {
      this._midrollFiring = false;
      this.resetPlayer();
    } else {
      this._exitAdMode();
      this.readAd();
    }
  };

  onAdError = () => {
    // Idempotent per ad: a media failure (e.g. a 404 mediaFile) can surface as BOTH a play()
    // rejection (handled in playLinearAd) and the element's 'error' event. Handle it once so
    // we don't fire the error beacon twice or advance/resume more than one step. The flag is
    // reset for each ad in _enterAdMode.
    if (this._adErrorHandled) return;
    this._adErrorHandled = true;
    this.debug('onAdError');
    if (this.linearVastTracker) {
      this.linearVastTracker.error({ ...this.macros, ERRORCODE: 405 }); // problem displaying MediaFile
    }
    const mediaError = this.adVideo?.error;
    this._captureError('SimpleVast: Ad playback error', {
      src: this.adVideo?.currentSrc || null,
      mediaErrorCode: mediaError?.code ?? null,
      mediaErrorMessage: mediaError?.message ?? null,
      networkState: this.adVideo?.networkState ?? null,
      readyState: this.adVideo?.readyState ?? null,
      playRejection: this._lastPlayRejection ? String(this._lastPlayRejection) : null,
    });
    this._lastPlayRejection = null;
    this._trigger('error', { message: 'SimpleVast: Ad playback error', tag: this.options.vastUrl });
    this.removeDomElements();

    if (this.adsArray.length === 0) {
      this._midrollFiring = false;
      this.resetPlayer();
    } else {
      this._exitAdMode();
      this.readAd();
    }
  };

  onAdEnded = () => {
    this.debug('onAdEnded');
    this.clearSkipInterval();
    this.linearVastTracker.complete({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
    });
    this.removeDomElements();

    if (this.adsArray.length === 0) {
      this.resetPlayer();
    } else {
      this._exitAdMode();
      this.readAd();
    }
  };

  onReadyForPostroll = async () => {
    this.debug('onReadyForPostroll');
    if (this.postRollUrl) {
      await this.handleVAST(this.postRollUrl);
      if (this.adsArray.length > 0) this.readAd();
    } else if (this.postRollData) {
      this.adsArray = this.postRollData;
      if (this.adsArray.length > 0) this.readAd();
    }
  };

  onContentEnded = () => {
    if (this.postRollUrl || this.postRollData) {
      this.onReadyForPostroll();
    } else {
      this.removeEventsListeners();
    }
  };

  onSkip = () => {
    this.debug('onSkip');
    this._trigger('skip');

    this.linearVastTracker.skip({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
    });

    this.removeDomElements();
    this.clearSkipInterval();

    if (this.adsArray.length === 0) {
      this.resetPlayer();
    } else {
      this._exitAdMode();
      this.readAd();
    }
  };

  onUnload = () => {
    if (!this.isInAdMode || !this.linearVastTracker) return;
    this.linearVastTracker.close({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
    });
    this.removeEventsListeners();
  };

  // ─── Player reset ─────────────────────────────────────────────────────────

  resetPlayer() {
    this.clearSkipInterval();
    this._exitAdMode();
    this._midrollFiring = false;
    this._trigger('complete');
    this._resumeContent();
  }

  // ─── Event registration (content-level) ──────────────────────────────────

  addEventsListeners() {
    this.video.addEventListener('ended', this.onContentEnded);
    document.addEventListener('fullscreenchange', this.onAdFullScreenChange);
    document.addEventListener('webkitfullscreenchange', this.onAdFullScreenChange);
    window.addEventListener('beforeunload', this.onUnload);
  }

  removeEventsListeners() {
    this.debug('removeEventsListeners');
    this.video.removeEventListener('ended', this.onContentEnded);
    this.video.removeEventListener('timeupdate', this.onProgress);
    this.video.removeEventListener('seeking', this._onContentSeeking);
    document.removeEventListener('fullscreenchange', this.onAdFullScreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onAdFullScreenChange);
    window.removeEventListener('beforeunload', this.onUnload);
  }

  // ─── Skip button ─────────────────────────────────────────────────────────

  addSkipButton(creative) {
    if (!this.options.addSkipButton || !(creative.skipDelay > 0)) return;

    const { skipDelay } = creative;
    let skipRemainingTime = Math.round(skipDelay - (this.adVideo?.currentTime ?? 0));
    let isSkippable = skipRemainingTime < 1;

    const svgString = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-left:0.5rem"><path d="M2.86839 13.7592C1.83655 14.328 1 13.862 1 12.7191L1 4.28512C1 3.14109 1.83655 2.67568 2.86839 3.244L10.5384 7.47165C11.5706 8.04071 11.5706 8.96267 10.5384 9.5316L2.86839 13.7592Z" fill="#1C1C1C"/><path d="M12.9146 3.91667C12.9146 3.41041 13.325 3 13.8312 3C14.3375 3 14.7479 3.41041 14.7479 3.91667V13.0833C14.7479 13.5896 14.3375 14 13.8312 14C13.325 14 12.9146 13.5896 12.9146 13.0833V3.91667Z" fill="#1C1C1C"/></svg>';
    const parser = new DOMParser();
    const svgEl = parser.parseFromString(svgString, 'image/svg+xml').documentElement;
    const spanEl = document.createElement('span');
    spanEl.textContent = 'رد کردن آگهی';

    const skipBtn = document.createElement('div');
    skipBtn.id = 'simplevast-skipButton';
    skipBtn.style.cssText = [
      'position:absolute', 'right:1.5rem', 'bottom:10.5rem',
      'min-width:9rem', 'width:10.75rem', 'height:2.5rem',
      'border-radius:0.5rem', 'z-index:1000', 'background:#f1f1f1',
      'color:#1c1c1c', 'display:flex', 'justify-content:center',
      'align-items:center', 'padding:0.5rem 1rem', 'cursor:not-allowed',
      'font-size:0.875rem', 'font-weight:500', 'user-select:none',
    ].join(';');

    this.domElements.push(skipBtn);
    this.video.parentElement.appendChild(skipBtn);

    const updateBtn = () => {
      if (isSkippable) {
        skipBtn.innerHTML = '';
        skipBtn.appendChild(svgEl);
        skipBtn.appendChild(spanEl);
      } else {
        const timeEl = document.createElement('span');
        timeEl.style.marginRight = '0.25rem';
        timeEl.textContent = `تا (${skipRemainingTime.toFixed()})`;
        skipBtn.innerHTML = '';
        skipBtn.appendChild(svgEl.cloneNode(true));
        skipBtn.appendChild(spanEl.cloneNode(true));
        skipBtn.appendChild(timeEl);
      }
    };

    // On mobile a bare <div> with only onclick often needs a second tap (the first is
    // swallowed as an activation/hover tap). Bind touchend as well and guard against the
    // duplicate synthesized click so the ad skips on the first tap without firing the VAST
    // skip beacon twice.
    this._skipTriggered = false;
    const handleSkipActivate = (e) => {
      if (e && e.cancelable) e.preventDefault();
      if (this._skipTriggered) return;
      this._skipTriggered = true;
      this.onSkip();
    };
    const bindSkip = () => {
      skipBtn.style.cursor = 'pointer';
      skipBtn.addEventListener('touchend', handleSkipActivate);
      skipBtn.addEventListener('click', handleSkipActivate);
    };

    updateBtn();
    if (isSkippable) {
      bindSkip();
    } else {
      this._skipInterval = setInterval(() => {
        const ct = this.adVideo?.currentTime ?? 0;
        skipRemainingTime = Math.round(skipDelay - ct);
        isSkippable = skipRemainingTime < 1;

        if (isSkippable) {
          bindSkip();
          this.clearSkipInterval();
        }

        updateBtn();
      }, 1000);
    }
  }

  clearSkipInterval = () => {
    clearInterval(this._skipInterval);
    this._skipInterval = null;
  };

  // ─── Ad click ────────────────────────────────────────────────────────────

  adClickCallback = (ctaUrl) => {
    this._trigger('click');
    window.open(ctaUrl, '_blank');
    this.linearVastTracker.click(null, {
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.adVideo?.currentTime ?? 0),
    });
  };

  // ─── Static helpers ───────────────────────────────────────────────────────

  static getBestMediaFile(mediaFilesAvailable, selectedContentQuality = undefined) {
    if (!mediaFilesAvailable || mediaFilesAvailable.length === 0) return null;

    const validMediaFiles = mediaFilesAvailable.filter((mediaFile) => mediaFile.fileURL);
    if (validMediaFiles.length === 0) return null;

    const contentQuality = Number(selectedContentQuality);
    if (contentQuality > 0) {
      const sameQualityMediaFile = validMediaFiles.find(
        (mediaFile) => SimpleVast.getMediaFileQuality(mediaFile) === contentQuality,
      );
      if (sameQualityMediaFile) return sameQualityMediaFile;

      return [...validMediaFiles].sort((a, b) => (
        Math.abs(SimpleVast.getMediaFileQuality(a) - contentQuality) -
        Math.abs(SimpleVast.getMediaFileQuality(b) - contentQuality)
      ) || (
        Math.abs((a.height || 0) - contentQuality) -
        Math.abs((b.height || 0) - contentQuality)
      ))[0];
    }

    const { height, width } = window.screen;
    const sorted = [...validMediaFiles].sort((a, b) => (
      Math.abs((a.height || 0) - height) - Math.abs((b.height || 0) - height)
    ) || (
      Math.abs((a.width || 0) - width) - Math.abs((b.width || 0) - width)
    ) || (
      Number(a.bitrate || 0) - Number(b.bitrate || 0)
    ),
    );

    return sorted[0];
  }

  static getMediaFileQuality(mediaFile) {
    const height = Number(mediaFile?.height || 0);

    if (height <= 240) return 240;
    if (height <= 360) return 360;
    if (height <= 480) return 480;
    if (height <= 720) return 720;
    return 1080;
  }

  static getBestCtaUrl(creative) {
    const url = creative?.videoClickThroughURLTemplate?.url;
    return isHttpUrl(url) ? url : false;
  }

  static getCloseButton(clickCallback) {
    const btn = document.createElement('button');
    btn.addEventListener('click', clickCallback);
    btn.style.cssText = [
      'width:20px', 'height:20px', 'position:absolute', 'right:5px', 'top:5px',
      'z-index:3', 'background:#CCC', 'color:#000', 'font-size:12px', 'cursor:pointer',
    ].join(';');
    btn.textContent = 'X';
    return btn;
  }

  static applyNonLinearCommonDomStyle(el) {
    el.style.cursor = 'pointer';
    el.style.left = '50%';
    el.style.position = 'absolute';
    el.style.transform = 'translateX(-50%)';
    el.style.bottom = '80px';
    el.style.display = 'block';
    el.style.zIndex = '11';
  }

  static getMidrolls(adBreaks) {
    if (!adBreaks) return [];
    return adBreaks
      .filter((b) => !PREROLL_OFFSETS.includes(b.timeOffset) && !POSTROLL_OFFSETS.includes(b.timeOffset))
      .map((b) => ({
        timeOffset: b.timeOffset,
        vastUrl: b.adSource.adTagURI?.uri,
        vastData: b.adSource.vastAdData,
      }));
  }

  static getPreroll(adBreaks) {
    if (!adBreaks) return [];
    return adBreaks.filter((b) => PREROLL_OFFSETS.includes(b.timeOffset));
  }

  static getPostroll(adBreaks) {
    if (!adBreaks) return null;
    return adBreaks.find((b) => POSTROLL_OFFSETS.includes(b.timeOffset)) || null;
  }

  debug(msg, data = undefined) {
    if (!this.options.debug) return;
    console.info('SimpleVast ---', msg, data ?? '');
  }

  // Reports a SimpleVast failure to Sentry. Never throws — an unavailable/broken Sentry
  // must not take playback down with it.
  _captureError(message, extra = {}) {
    Sentry.withScope((scope) => {
      scope.setTag('Tech', 'MP4');
      scope.setTag('Player', 'simple');
      scope.setTag('Plugin', 'simple-vast');
      scope.setExtras({
        vmapUrl: this.options.vmapUrl || null,
        ...extra,
      });
      if (extra.error instanceof Error) {
        scope.setExtra('vastMessage', message);
        Sentry.captureException(extra.error);
      } else {
        Sentry.captureMessage(message, 'error');
      }
    });
  }

  dispose() {
    this._disposed = true;
    clearTimeout(this._adLoadTimeout);
    this._adLoadTimeout = null;
    this.adsArray = [];
    this.watchForProgress = [];
    this.postRollUrl = null;
    this.postRollData = null;
    this.clearSkipInterval();
    this.removeEventsListeners();
    this._exitAdMode();
    this.removeDomElements();
    this.video.removeEventListener('canplay', this._onContentCanPlay);
    this.video.removeEventListener('play', this._onContentPlayAttempt);
    this.video.removeEventListener('volumechange', this._onContentVolumeChange);
    if (this.adVideo) {
      this.adVideo.pause();
      this.adVideo.removeAttribute('src');
      this.adVideo.remove();
      this.adVideo = null;
    }
    if (this._adPlaceholder) {
      this._adPlaceholder.remove();
      this._adPlaceholder = null;
    }
  }
}

SimpleVast.prototype.playLinearAd = playLinearAd;
SimpleVast.prototype.playNonLinearAd = playNonLinearAd;
SimpleVast.prototype.playCompanionAd = playCompanionAd;
SimpleVast.prototype.addIcons = addIcons;
SimpleVast.prototype.handleVMAP = handleVMAP;
SimpleVast.prototype.parseInlineVastData = parseInlineVastData;

export default SimpleVast;
